// Employee Companion session state — the transcript and, critically, the
// RED-FLAG SESSION LOCK.
//
// WHY THIS ISN'T `useState` IN THE COMPONENT (2026-07-31). `locked` is a
// safety surface, not a UI convenience: it is set when a turn comes back with
// `sessionEnded: true` (a red flag fired), and it is what swaps the composer
// for the crisis handoff card (DESIGN §5.6.3 #12). The server independently
// enforces the same state — a further turn gets `409 session_locked` — so a
// client that forgets the lock does not become *unsafe*, but it does show a
// grieving employee a blank, apparently-working chat where the crisis
// resources used to be, seconds after they disclosed something serious. That
// is the failure this hook exists to make structurally impossible.
//
// It lived in `useState`, which meant any unmount cleared it. `PortalMobileShell`
// worked around that by keeping the chat permanently mounted and merely
// CSS-hiding it off-tab — a real fix for tab switches, but only for tab
// switches. It could not survive the Employee portal's viewport branch
// swapping component trees, which is exactly what the responsive rollout
// does. Holding the state in the TanStack query cache instead makes it
// independent of the component tree altogether, so the workaround is no
// longer load-bearing and any future layout change is safe by default.
//
// The write happens inside `mutationFn`, NOT `onSuccess`. TanStack removes a
// mutation observer when its component unmounts and does not fire
// component-level callbacks afterwards — so if the employee switched tab,
// rotated the phone, or navigated while the turn that fired the red flag was
// in flight, `onSuccess` never ran and the lock was simply lost. `mutationFn`
// runs to completion regardless.
//
// SCOPE, deliberately unchanged: this is still session state, not persisted
// state. The query cache is in-memory, so a full page reload starts a fresh
// client-side session exactly as it did before — the server is the authority
// across reloads. `queryClient.clear()` on sign-out wipes it.
//
// ONE KEY, NO `caseId`. A Companion session is per-employee (one conversation
// Durable Object), and the lock is a property of that session, not of a case
// — `caseId` is only passed for resource-access attribution. Keying it here
// means two mounted instances share one transcript and one lock rather than
// silently diverging, which the portal's several layouts made possible.

import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EmployeeCompanionResponse } from '@besideu/shared/maeve';
import { api, ApiError } from '@/lib/api.ts';

export const COMPANION_SESSION_KEY = ['employee', 'maeve', 'session'] as const;
export const DISCLOSURE_QUERY_KEY = ['employee', 'maeve', 'disclosure'] as const;

type Aggregate = EmployeeCompanionResponse['aggregate_classification'];

export interface VisibleTurn {
  role: 'user' | 'assistant';
  text: string;
  /** Only meaningful for assistant turns; `null` on user turns. */
  aggregate: Aggregate | null;
  sessionEnded: boolean;
}

export interface CompanionSession {
  history: VisibleTurn[];
  /**
   * The unsent message. Cached alongside the transcript for the same reason:
   * the Employee portal swaps component trees at `lg`, so on `useState` a
   * rotation silently binned whatever the employee was part-way through
   * writing — which, on this surface, may have taken them a while to find the
   * words for.
   */
  draft: string;
  /**
   * The §15 `prefill` opener already seeded into `draft`, or null. Seeding is
   * once-per-VALUE, not once-per-mount. `prefill` is derived from a `?moment=`
   * URL param that survives reloads and remounts, so a mount-scoped guard
   * (the old `seeded` ref) re-injected the opener every time the viewport
   * crossed the breakpoint — on top of a transcript that already contained it.
   */
  seededPrefill: string | null;
  /**
   * Set once a turn returns `sessionEnded: true`. MONOTONIC — never cleared
   * by anything short of a reload or sign-out. There is deliberately no
   * `unlock`: the only path forward after a red flag is the handoff card.
   */
  locked: boolean;
}

const EMPTY_SESSION: CompanionSession = {
  history: [],
  locked: false,
  draft: '',
  seededPrefill: null,
};

export function useCompanionChat(prefill?: string) {
  const queryClient = useQueryClient();

  // The query cache as a client-state store. `queryFn` seeds an empty session
  // once and never runs again: every refetch trigger is off and both times are
  // `Infinity`, because a refetch would resolve to `EMPTY_SESSION` and clear
  // the lock — the precise outcome this indirection prevents. Writes go
  // through `setQueryData`.
  const { data: session = EMPTY_SESSION } = useQuery<CompanionSession>({
    queryKey: COMPANION_SESSION_KEY,
    queryFn: () => EMPTY_SESSION,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  function patch(next: (cur: CompanionSession) => CompanionSession) {
    queryClient.setQueryData<CompanionSession>(COMPANION_SESSION_KEY, (cur = EMPTY_SESSION) =>
      next(cur),
    );
  }

  // Seed the §15 opener in an effect, not during render: `setQueryData`
  // notifies observers synchronously, and a render-phase write can update a
  // different subscribed component mid-render. Skipped once the session is
  // locked — after a red flag the only path forward is the handoff card, so
  // pre-filling a composer that isn't rendered would be meaningless.
  //
  // Gated ONLY on the prefill value, deliberately. An earlier version also
  // bailed when the transcript was non-empty, which broke the case this exists
  // for: tapping "talk to Maeve about this moment" on a SECOND touch point
  // navigates to `?moment=B` mid-conversation and must seed B's opener. The
  // value guard alone gives both halves — a remount with an unchanged
  // `?moment=` doesn't re-inject, a genuinely new moment does.
  useEffect(() => {
    if (!prefill || session.seededPrefill === prefill || session.locked) return;
    patch((cur) => ({ ...cur, draft: prefill, seededPrefill: prefill }));
  }, [prefill, session.seededPrefill, session.locked]);

  const send = useMutation({
    mutationFn: async (text: string): Promise<EmployeeCompanionResponse> => {
      const resp = await api.post<EmployeeCompanionResponse>('/api/employee/maeve/turn', { text });
      // Commit inside `mutationFn` — see the file header. This is the line
      // that must not move into `onSuccess`.
      patch((cur) => ({
        ...cur,
        history: [
          ...cur.history,
          { role: 'user', text, aggregate: null, sessionEnded: false },
          {
            role: 'assistant',
            text: resp.message,
            aggregate: resp.aggregate_classification,
            sessionEnded: resp.sessionEnded,
          },
        ],
        // `||`, never `=`: a lock already set stays set even if a later
        // response omits the flag.
        locked: cur.locked || resp.sessionEnded,
        draft: '',
      }));
      return resp;
    },
    onSuccess: () => {
      // Defensive: a prior error card from a transient failure (e.g. 502)
      // would otherwise stay mounted until the user clicks Dismiss. The
      // textarea disable on `isPending` means a stale error can't co-exist
      // with an in-flight send today, but clearing on success keeps that
      // guarantee independent of future textarea-disable changes.
      send.reset();
    },
    onError: (err) => {
      // `disclosure_required` (409) means the session-start gate fired on the
      // server. The expected UX is to re-show the disclosure modal, not to ask
      // the user to refresh — invalidating the disclosure query causes
      // `EmployeeCompanionChat` to re-render into the gate branch on the next
      // pass. The error card never appears for this path because `TurnError`
      // is gated on `send.error` and we don't keep this error in state.
      if (err instanceof ApiError && err.status === 409 && err.message === 'disclosure_required') {
        void queryClient.invalidateQueries({ queryKey: DISCLOSURE_QUERY_KEY });
        send.reset();
      }
    },
  });

  function submit() {
    const trimmed = session.draft.trim();
    if (trimmed.length === 0 || send.isPending || session.locked) return;
    send.mutate(trimmed);
  }

  return {
    history: session.history,
    locked: session.locked,
    send,
    draft: session.draft,
    setDraft: (v: string) => patch((cur) => ({ ...cur, draft: v })),
    submit,
  };
}
